import React, { useEffect, useRef, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Button } from "primereact/button";
import { SplitButton } from "primereact/splitbutton";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";

import { useMediaQuery } from "react-responsive";
import QuoteSelectionView from "./QuoteSelectionView";
import PreviewValoresToggle from "./PreviewValoresToggle";
import QuoteReserveWizard from "./QuoteReserveWizard";
import VoucherButton from "./VoucherButton";
import { formatNumberCurrency, formatDateBR } from "./Utils";
import { publicBaseUrl } from "../Utils/publicUrl";
import { descreverCambio } from "../Utils/cambioJanela";
import { compararValores, impedimentosDoPreview, selecionadosDaCotacao, motivoTexto } from "../Utils/previewValores";
import { printQuote, viewQuotePdf, fetchQuotePdfUrl } from "../Utils/quotePdf";
import imagePlaceHolder from "../Assets/Images/noimg.jpg";
import {
	QUOTE_STATUS_F2,
	quoteF2StatusLabel,
	quoteF2StatusSeverity,
	quoteF2Acoes,
	quoteLossMotivoLabel,
	QUOTE_LOSS_MOTIVO_OPTIONS,
	agruparTrechos,
	desfechoPorTrechoDe,
} from "../Constants/quote";
import * as QuoteActions from "../Store/Actions/Quote";

/**
 * QuoteFase2Panel — painel de AÇÕES da agência para a Cotação Fase 2.
 * Conectado ao Redux (lê `selectedQuote`, despacha os thunks Fase 2 que hoje
 * delegam ao mock e amanhã à API — ver Store/Actions/Quote.js). Reutilizado
 * pelo harness (/admin/cotacoes-fase2) e pelo QuoteDetailModal real (atrás da
 * flag). `allowDevSim` liga o bloco dev de simular pagamento (só no harness).
 */
const QuoteFase2Panel = (props) => {
	const { selectedQuote: quote, allowDevSim } = props;
	const toast = useRef(null);
	const [sendOpen, setSendOpen] = useState(false);
	const [perdaOpen, setPerdaOpen] = useState(false);
	const [clienteOpen, setClienteOpen] = useState(false);
	const [reservaOpen, setReservaOpen] = useState(false);
	const [verificandoReserva, setVerificandoReserva] = useState(false);

	// Abre o wizard SÓ depois de conferir que dá para reservar (owner, 05/09).
	//
	// Sem isto, o operador preenchia responsável e hóspedes de N reservas para o `proceed` recusar
	// no fim com ITEM_UNAVAILABLE — o backend aborta o lote inteiro quando um item cai, então o
	// trabalho era perdido por completo. A conferência usa o MESMO endpoint de preview, que aplica
	// os mesmos dois gates do booking (disponibilidade + política de cancelamento).
	//
	// Falha de rede NÃO bloqueia: barrar o operador porque a conferência caiu seria pior que deixar
	// seguir — o backend continua recusando na hora de reservar, que é a trava que vale.
	const abrirReserva = async () => {
		setVerificandoReserva(true);
		try {
			const cmp = compararValores(selecionadosDaCotacao(quote), await props.previewValoresCotacao(quote.id));
			const bloqueios = impedimentosDoPreview(cmp);
			if (bloqueios.length) {
				confirmDialog({
					header: "Esta cotação não pode ser reservada agora",
					icon: "pi pi-exclamation-triangle",
					style: { width: "min(560px, 94vw)" },
					message: (
						<div style={{ textAlign: "left" }}>
							<p style={{ marginTop: 0 }}>Conferimos agora no fornecedor e {bloqueios.length === 1 ? "um item selecionado não pode ser reservado" : `${bloqueios.length} itens selecionados não podem ser reservados`}:</p>
							<ul style={{ paddingLeft: 18, margin: "0 0 12px" }}>
								{bloqueios.map((b) => <li key={b.id} style={{ marginBottom: 4 }}><strong>{b.hotel}</strong> — {motivoTexto(b)}</li>)}
							</ul>
							<p style={{ margin: 0 }}>Como a reserva é feita em bloco, <strong>se um item falhar todos são abortados</strong> — preencher os hóspedes agora seria trabalho perdido. Reabra para ajuste, troque ou remova o que saiu, e aprove de novo.</p>
						</div>
					),
					acceptLabel: "Reabrir para ajuste",
					rejectLabel: "Fechar",
					accept: () => run(props.reabrirCotacaoParaAjuste(quote.id, QUOTE_STATUS_F2.AGUARDANDO_SELECAO)
						.then(() => say("Reaberto para nova seleção — cliente notificado")).catch(fail)),
				});
				return;
			}
		} catch (e) {
			toast.current?.show({ severity: "warn", summary: "Não foi possível conferir a disponibilidade",
				detail: "Seguindo assim mesmo. Se algum item tiver saído, a reserva será recusada ao finalizar.", life: 6000 });
		} finally { setVerificandoReserva(false); }
		setReservaOpen(true);
	};
	const [reenviarOpen, setReenviarOpen] = useState(false);
	const [recotando, setRecotando] = useState(false);
	const [acting, setActing] = useState(false); // #6 — loading das ações que batem no endpoint
	const [pdfOpen, setPdfOpen] = useState(false);
	const [pdfUrl, setPdfUrl] = useState(null);
	const [editingName, setEditingName] = useState(false);
	const [nameDraft, setNameDraft] = useState("");
	const isMobile = useMediaQuery({ query: "(max-width: 1023px)" });

	// Re-cota ao abrir SÓ no rascunho (o /quote/refresh do BE é rascunho-only; nos demais
	// status daria 409). Best-effort: erro não estoura a tela.
	const quoteId = quote?.id;
	const podeRecotar = quote?.status === QUOTE_STATUS_F2.RASCUNHO && (quote?.itens || []).length > 0;
	useEffect(() => {
		if (!quoteId || !podeRecotar) return;
		setRecotando(true);
		const minVisible = new Promise((r) => setTimeout(r, 500));
		Promise.all([Promise.resolve(props.recotarCotacaoF2(quoteId)).catch(() => {}), minVisible]).finally(() => setRecotando(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [quoteId]);

	if (!quote) return null;
	const acoes = quoteF2Acoes(quote.status);
	const say = (summary) => toast.current?.show({ severity: "success", summary, life: 2500 });
	// Erro — mapeia os 409/500 estruturados do BE (proceed-reservation e transições).
	const fail = (e) => {
		const d = e?.response?.data || {};
		const code = d.error || d.code;
		// BUG-004: o BE passou a recusar tarifa sem 2 dias úteis de janela de cancelamento gratuito, e
		// a recusa volta dentro do mesmo ITEM_UNAVAILABLE. Merece texto próprio: aqui a tarifa EXISTE e
		// tem preço — falta janela. Quem lê "ficou indisponível" tenta de novo e não entende por quê.
		const semJanela = (d.unavailable || []).filter((i) => i?.motivo === "sem_cancelamento_gratuito");
		const hoteis = semJanela.map((i) => i.hotelNome).filter(Boolean).join(", ");
		const um = semJanela.length === 1;
		// "Não conseguimos LER a política" é diferente de "a tarifa NÃO TEM cancelamento gratuito":
		// no primeiro caso o operador não fez nada errado e não há o que ajustar na tarifa.
		const ilegivel = semJanela.some((i) => i?.code === "POLITICA_DESCONHECIDA");
		const detail = semJanela.length
				? `${um ? "Uma opção não pôde" : `${semJanela.length} opções não puderam`} ser reservada${um ? "" : "s"}: `
					+ (ilegivel
						? "não foi possível ler a política de cancelamento da tarifa, e sem isso não há segurança sobre cobrança em caso de cancelamento"
						: "a tarifa não permite cancelamento gratuito com a antecedência mínima exigida")
					+ `${hoteis ? ` (${hoteis})` : ""}. Escolha outra opção para ${um ? "esse trecho" : "esses trechos"}.`
			: code === "ITEM_UNAVAILABLE"
				? "Um ou mais itens ficaram indisponíveis. Reabra a seleção e ajuste as opções."
			: code === "RESERVATION_PARTIAL_ROLLBACK"
				? "Falha ao criar parte das reservas — todas foram desfeitas. Tente novamente."
			: code === "RESERVATION_ROLLBACK_INCOMPLETE"
				? "Falha na reserva e a reversão não completou. Acione o suporte."
			// Aprovação parcial (BE 2026-08-03): a regra é "ao menos uma seleção".
			: code === "EMPTY_SELECAO"
				? "Selecione ao menos uma opção para aprovar."
			: code === "NO_ITEMS"
				? "Adicione ao menos uma opção à cotação antes de enviar."
			: (d.errorMessage || d.message || "Não foi possível concluir a ação. Tente novamente.");
		toast.current?.show({ severity: "error", summary: "Erro", detail, life: 6000 });
	};
	// #6 — envolve uma ação (thunk + re-retrieve) com loading global dos botões.
	const run = (p) => { setActing(true); return Promise.resolve(p).finally(() => setActing(false)); };

	// #7 — PDF real da cotação (mesmo util do modal legado). Desktop: modal com iframe;
	// mobile: nova aba. Baixar via download.
	const handleViewPdf = () => {
		if (isMobile) { setActing(true); viewQuotePdf(quote).catch(fail).finally(() => setActing(false)); return; }
		setActing(true);
		fetchQuotePdfUrl(quote).then((url) => { setPdfUrl(url); setPdfOpen(true); }).catch(fail).finally(() => setActing(false));
	};
	const handleDownloadPdf = () => { setActing(true); printQuote(quote).catch(fail).finally(() => setActing(false)); };
	const closePdf = () => { setPdfOpen(false); setPdfUrl((u) => { if (u) URL.revokeObjectURL(u); return null; }); };

	// #8 — edição inline do nome (só rascunho), reusa o thunk renameQuote.
	const startEditName = () => { setNameDraft(quote.nome || ""); setEditingName(true); };
	const saveName = () => {
		const n = nameDraft.trim();
		setEditingName(false);
		if (n && n !== quote.nome) run(props.renameQuote(quote.id, n).then(() => say("Nome atualizado")).catch(fail));
	};

	const publicUrl = `${publicBaseUrl()}/cotacao/${quote.token || ""}`;
	const roxo = { background: "#814DE5", border: "none" };

	// 2026-08-03 — não existe mais trecho obrigatório: todo bloco é opcional e a
	// única regra é "ao menos uma opção selecionada" (validada no aprovar). Isso
	// habilita a aprovação PARCIAL da cotação.
	const trechos = agruparTrechos(quote);
	const temItens = trechos.some((t) => (t.opcoes || []).length > 0);
	// "Reenviar e-mail" só existe se um e-mail chegou a ser disparado. Se a agência
	// pulou o envio (avançar sem enviar), não há o que reenviar. O histórico de
	// eventos é a fonte da verdade; sem eventos, cai no lead cadastrado.
	const eventos = quote.eventos || [];
	const emailFoiEnviado = eventos.length
		? eventos.some((ev) => ["enviada", "reenviada"].includes(ev.tipo || ev.type))
		: !!quote.contato?.email;
	// Item 8 — a página do cliente é acessível a partir de "aguardando seleção".
	// PERDIDA incluída (owner 2026-08-05): o BE passou a servir o snapshot nesse
	// status em vez de 404, e a agência precisa ver o que o cliente vê ao abrir o
	// link de uma cotação encerrada.
	// Status em que a cotação JÁ passou pela reserva — logo, existem reservas mesmo que o
	// retrieve não as devolva. `perdida` só conta quando a perda se deu no estágio reservada
	// (cancelamento); perda antes disso nunca teve reserva.
	const temReservaEsperada = [
		QUOTE_STATUS_F2.RESERVADA, QUOTE_STATUS_F2.GANHA, QUOTE_STATUS_F2.PARCIALMENTE_GANHA,
	].includes(quote.status) || (quote.status === QUOTE_STATUS_F2.PERDIDA && quote.perda?.estagio === QUOTE_STATUS_F2.RESERVADA);

	const publicAcessivel = [
		QUOTE_STATUS_F2.AGUARDANDO_SELECAO, QUOTE_STATUS_F2.APROVADA, QUOTE_STATUS_F2.RESERVADA,
		QUOTE_STATUS_F2.GANHA, QUOTE_STATUS_F2.PARCIALMENTE_GANHA, QUOTE_STATUS_F2.PERDIDA,
	].includes(quote.status);

	// Item 7 — desfazer envio pede confirmação.
	const confirmarDesfazer = () => confirmDialog({
		message: "A cotação volta para rascunho e o link do cliente é desativado (se um e-mail foi enviado, ele deixa de funcionar). Deseja continuar?",
		header: "Voltar para rascunho", icon: "pi pi-undo", style: CONFIRM_STYLE,
		acceptLabel: "Sim, voltar", rejectLabel: "Cancelar",
		accept: () => run(props.desfazerEnvioCotacao(quote.id).then(() => say("Cotação voltou para rascunho")).catch(fail)),
	});
	// Item 3 — avançar para seleção sem enviar e-mail (agência opera pelo cliente).
	const confirmarAvancar = () => confirmDialog({
		message: "A cotação vai para 'Aguardando seleção' SEM enviar e-mail ao cliente. Use quando você mesmo for selecionar/aprovar em nome dele. Continuar?",
		header: "Ir para a seleção sem enviar", icon: "pi pi-forward", style: CONFIRM_STYLE,
		acceptLabel: "Sim, avançar", rejectLabel: "Cancelar",
		accept: () => run(props.avancarCotacaoSemEnviar(quote.id).then(() => say("Avançado para seleção — sem e-mail")).catch(fail)),
	});
	// Excluir a cotação (rascunho).
	const confirmarExcluir = () => confirmDialog({
		message: "Excluir esta cotação? Esta ação não pode ser desfeita.",
		header: "Excluir cotação", icon: "pi pi-trash", style: CONFIRM_STYLE,
		acceptLabel: "Excluir", rejectLabel: "Cancelar", acceptClassName: "p-button-danger",
		accept: () => run(props.deleteQuote(quote.id).then(() => say("Cotação excluída")).catch(fail)),
	});
	// Remover uma opção (item) do rascunho.
	const confirmarRemoverItem = (op) => confirmDialog({
		message: `Remover "${op?.hotel?.nome || "opção"} — ${op?.quarto?.nome || ""}" da cotação?`,
		header: "Remover opção", icon: "pi pi-trash", style: CONFIRM_STYLE,
		acceptLabel: "Remover", rejectLabel: "Cancelar", acceptClassName: "p-button-danger",
		accept: () => run(props.removerItemCotacao(quote.id, op.id).then(() => say("Opção removida")).catch(fail)),
	});

	return (
		<div>
			<Toast ref={toast} />
			<ConfirmDialog />

			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
				<div>
					{editingName ? (
						<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
							<InputText value={nameDraft} autoFocus disabled={acting} onChange={(e) => setNameDraft(e.target.value)}
								onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }} style={{ minWidth: 240 }} />
							<Button icon="pi pi-check" rounded text aria-label="Salvar nome" onClick={saveName} loading={acting} />
							<Button icon="pi pi-times" rounded text severity="secondary" aria-label="Cancelar" onClick={() => setEditingName(false)} disabled={acting} />
						</span>
					) : (
						<h2 style={{ margin: 0, color: "#2E0249", display: "inline-flex", alignItems: "center", gap: 6 }}>
							{quote.nome}
							{quote.status === QUOTE_STATUS_F2.RASCUNHO ? <Button icon="pi pi-pencil" rounded text size="small" aria-label="Editar nome" onClick={startEditName} /> : null}
						</h2>
					)}
					{quote.createdBy?.agencyName ? (
						<div style={{ color: "#6B6B6B", fontSize: "0.9rem" }}>{quote.createdBy.agencyName} · criada por {quote.createdBy?.nome}</div>
					) : null}
				</div>
				<Tag value={quoteF2StatusLabel(quote.status)} severity={quoteF2StatusSeverity(quote.status)} style={{ fontSize: "0.95rem" }} />
			</div>

			{quote.status === QUOTE_STATUS_F2.PERDIDA && quote.perda ? (
				<Message severity="error" style={{ width: "100%", marginTop: 12 }}
					text={`Perdida (${quoteLossMotivoLabel(quote.perda.motivo)}) — no estágio "${quoteF2StatusLabel(quote.perda.estagio)}".`} />
			) : null}

			{quote.contato?.email ? (
				<div style={{ marginTop: 12, color: "#444", fontSize: "0.9rem" }}>
					<i className="pi pi-user" /> {quote.contato.nome} · {quote.contato.email}
					{quote.contato.documento ? ` · ${quote.contato.documento}` : ""}
					{quote.contato.telefone ? ` · ${quote.contato.telefone}` : ""}
					{quote.visualizadoEm ? <span style={{ color: "#2E9E5B" }}> · visualizou ✓</span> : null}
				</div>
			) : null}

			<Divider />

			{recotando ? (
				<div style={{ display: "flex", alignItems: "center", gap: 8, color: "#7A5FC0", fontSize: "0.85rem", marginBottom: 8 }}>
					<i className="pi pi-spin pi-spinner" /> Atualizando disponibilidade, preços e câmbio…
				</div>
			) : null}
			<CambioBanner quote={quote} />

			<AvisoAprovacaoVelha quote={quote} />

			{/* Opções da cotação com dados do hotel (sem lista de trechos separada e sem
			    marcação obrigatório/opcional — todo bloco é opcional). Itens 1/2/5. */}
			{[QUOTE_STATUS_F2.RASCUNHO, QUOTE_STATUS_F2.AGUARDANDO_SELECAO].includes(quote.status) ? (
				<div style={{ marginTop: 8 }}>
					<div style={{ fontSize: "0.8rem", color: "#888", margin: "4px 0 10px" }}>
						Abaixo são as opções da cotação que o cliente vai visualizar. Ele poderá aprovar uma opção
						de cada bloco - o que não for selecionado é descartado e não segue para reserva
						{/* Em "aguardando seleção" o complemento depende de ter havido envio:
						    sem e-mail, quem seleciona é a própria agência. */}
						{quote.status === QUOTE_STATUS_F2.AGUARDANDO_SELECAO ? (
							<div style={{ marginTop: 4 }}>
								{emailFoiEnviado
									? "Estas são as opções que o cliente está vendo no link enviado."
									: "Estas são as opções disponíveis para seleção."}
							</div>
						) : null}
					</div>
					<QuoteSelectionView quote={quote} preview
						editableTrechos={quote.status === QUOTE_STATUS_F2.RASCUNHO}
						onRemoveItem={quote.status === QUOTE_STATUS_F2.RASCUNHO ? confirmarRemoverItem : undefined} />
				</div>
			) : null}

			{/* Item 6 — seleção aprovada ACIMA dos botões (padrão das outras telas). */}
			{[QUOTE_STATUS_F2.APROVADA, QUOTE_STATUS_F2.RESERVADA, QUOTE_STATUS_F2.GANHA, QUOTE_STATUS_F2.PARCIALMENTE_GANHA].includes(quote.status) ? (
				<SelecaoAprovada quote={quote} onPreview={props.previewValoresCotacao} />
			) : null}

			{/* Perdida — mostra o conteúdo em leitura (bug: antes abria sem hotel/itens). */}
			{quote.status === QUOTE_STATUS_F2.PERDIDA ? <SelecaoPerdida quote={quote} /> : null}

			{/* Ações. Ordem por status (owner 2026-08-04): no rascunho as duas ações de
			    avanço vêm PRIMEIRO e PDF/Excluir descem para uma segunda linha; nos demais
			    status o PDF (quando existe) vai por último, à direita. */}
			<div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
				{acoes.enviarSelecao ? <Button label="Enviar cotação para o cliente escolher" icon="pi pi-send" disabled={!temItens || acting} onClick={() => setSendOpen(true)} style={roxo} /> : null}
				{acoes.enviarSelecao ? <Button label="Escolher sem enviar para o cliente" icon="pi pi-forward" outlined loading={acting} disabled={!temItens || acting} onClick={confirmarAvancar} /> : null}
				{acoes.desfazerEnvio ? <Button label="Voltar para rascunho" icon="pi pi-undo" outlined loading={acting} disabled={acting} onClick={confirmarDesfazer} /> : null}
				{acoes.reenviarEmail && emailFoiEnviado ? <Button label="Reenviar e-mail" icon="pi pi-envelope" outlined disabled={acting} onClick={() => setReenviarOpen(true)} /> : null}
				{acoes.operarComoCliente ? <Button label="Escolher para o cliente" icon="pi pi-user-edit" outlined disabled={acting} onClick={() => setClienteOpen(true)} /> : null}
				{acoes.prosseguirReserva ? <Button label="Prosseguir com reserva" icon="pi pi-check-circle" loading={verificandoReserva} disabled={acting || verificandoReserva} onClick={abrirReserva} style={roxo} /> : null}
				{acoes.reabrirAjuste ? (
					<SplitButton label="Reabrir para ajuste" icon="pi pi-pencil" outlined loading={acting} disabled={acting}
						onClick={() => run(props.reabrirCotacaoParaAjuste(quote.id, QUOTE_STATUS_F2.AGUARDANDO_SELECAO).then(() => say("Reaberto para nova seleção — cliente notificado")).catch(fail))}
						model={[
							{ label: "Trocar entre opções (→ seleção)", command: () => run(props.reabrirCotacaoParaAjuste(quote.id, QUOTE_STATUS_F2.AGUARDANDO_SELECAO).then(() => say("Reaberto para seleção")).catch(fail)) },
							{ label: "Remontar opções (→ rascunho)", command: () => run(props.reabrirCotacaoParaAjuste(quote.id, QUOTE_STATUS_F2.RASCUNHO).then(() => say("Voltou para rascunho")).catch(fail)) },
						]} />
				) : null}
				{acoes.marcarPerdida ? <Button label="Marcar como perdida" icon="pi pi-times" severity="danger" outlined disabled={acting} onClick={() => setPerdaOpen(true)} /> : null}
				{acoes.baixarPdf && quote.status !== QUOTE_STATUS_F2.RASCUNHO ? (
					<SplitButton label="Visualizar PDF" icon="pi pi-file-pdf" outlined loading={acting} disabled={acting}
						onClick={handleViewPdf}
						model={[{ label: "Baixar PDF", icon: "pi pi-download", command: handleDownloadPdf }]} />
				) : null}
			</div>
			{quote.status === QUOTE_STATUS_F2.RASCUNHO ? (
				<div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
					{acoes.baixarPdf ? (
						<SplitButton label="Visualizar PDF" icon="pi pi-file-pdf" outlined loading={acting} disabled={acting}
							onClick={handleViewPdf}
							model={[{ label: "Baixar PDF", icon: "pi pi-download", command: handleDownloadPdf }]} />
					) : null}
					<Button label="Excluir cotação" icon="pi pi-trash" severity="danger" outlined disabled={acting} onClick={confirmarExcluir} />
				</div>
			) : null}
			{quote.status === QUOTE_STATUS_F2.PERDIDA ? (
				<small style={{ color: "#999", display: "block", marginTop: 8 }}>
					<i className="pi pi-info-circle" /> Cotação perdida é um registro de histórico do funil e não reabre. Para uma nova tentativa, crie uma nova cotação.
				</small>
			) : null}

			{quote.reservas?.length ? (
				<Reservas quote={quote} say={say} allowDevSim={allowDevSim} onSimular={props.simularStatusReservaCotacao} />
			) : temReservaEsperada ? (
				/* A cotação passou pela reserva, mas o retrieve não devolve `reservas[]` (só o
				   proceed devolve) — então o bloco sumia sem explicação ao reabrir a cotação.
				   Enquanto o BE não inclui o campo (handoff frontend-cotacao-multiplas-reservas-
				   2026-08-07 §B), aponta o operador para onde o dado existe hoje. */
				<div style={{ marginTop: 16 }}>
					<Divider />
					<strong style={{ color: "#2E0249" }}>Reservas</strong>
					<div style={{ color: "#8A8A8A", fontSize: "0.85rem", marginTop: 6 }}>
						Os localizadores e o status de pagamento desta cotação estão no
						<strong> link do cliente</strong> e no módulo de reservas.
					</div>
				</div>
			) : null}

			{publicAcessivel && quote.token ? (
				<div style={{ marginTop: 14 }}>
					<Button label="Acessar link do cliente" icon="pi pi-external-link" text onClick={() => window.open(publicUrl, "_blank", "noopener")} />
				</div>
			) : publicAcessivel && !quote.token ? (
				<small style={{ color: "#B26A00", display: "block", marginTop: 14 }}>
					<i className="pi pi-exclamation-triangle" /> Link do cliente indisponível — o token público não veio na resposta do backend.
				</small>
			) : null}

			<HistoricoEventos quote={quote} />

			<SendModal open={sendOpen} onHide={() => setSendOpen(false)} quote={quote} say={say} fail={fail} onEnviar={props.enviarCotacaoParaSelecao} />
			<ReenviarModal open={reenviarOpen} onHide={() => setReenviarOpen(false)} quote={quote} say={say} fail={fail} onReenviar={props.reenviarEmailCotacao} />
			<PerdaModal open={perdaOpen} onHide={() => setPerdaOpen(false)} quote={quote} say={say} fail={fail} onPerder={props.marcarCotacaoPerdida} />
			<ClienteModal open={clienteOpen} onHide={() => setClienteOpen(false)} quote={quote} say={say} fail={fail} onAprovar={props.aprovarCotacaoComoAgencia} />
			<ReservaModal open={reservaOpen} onHide={() => setReservaOpen(false)} quote={quote} say={say} fail={fail} onProsseguir={props.prosseguirComReserva} />
			<Dialog header="PDF da cotação" visible={pdfOpen} onHide={closePdf} style={{ width: "min(960px, 96vw)", height: "92vh" }} blockScroll>
				{pdfUrl ? <iframe title="PDF da cotação" src={pdfUrl} style={{ width: "100%", height: "100%", minHeight: "70vh", border: 0 }} /> : null}
			</Dialog>
		</div>
	);
};

// ---- Sub-blocos -----------------------------------------------------------
// Câmbio do dia aplicado aos valores da cotação + disclaimer (vale só hoje).
const CambioBanner = ({ quote }) => {
	const [detalhe, setDetalhe] = useState(false);
	const c = quote?.cambio;
	if (!c?.usdBrl) return null;
	// Pago (venda ganha) → câmbio CONGELADO no pagamento: não mostra o aviso de variação.
	if (quote.status === QUOTE_STATUS_F2.GANHA) return null;
	// Reservada / parcialmente: a reserva já está feita no fornecedor — só o valor em reais
	// varia com o câmbio, até o pagamento. Pré-reserva: preços/câmbio ainda re-cotam.
	const posReserva = [QUOTE_STATUS_F2.RESERVADA, QUOTE_STATUS_F2.PARCIALMENTE_GANHA].includes(quote.status);
	// Cotação com reservas em estados diferentes: parte já paga (valor fechado), parte não.
	// Sem dizer isso, o aviso de variação parecia valer também para o que o cliente já pagou.
	const temPaga = (quote.reservas || []).some((r) => r.status === "paga");
	const temPendente = (quote.reservas || []).some((r) => r.status === "pendente");
	// Uma LINHA por padrão, detalhe sob demanda (owner 05/09): a taxa e a validade respondem a
	// pergunta do dia a dia; o resto é contexto que só interessa quando alguém duvida do número.
	// Três parágrafos fixos no topo empurravam para baixo o que o operador veio ver.
	const j = descreverCambio(c);
	if (!j) return null;
	return (
		<div style={cambioBox}>
			{/* Colapsado já traz a frase inteira (owner 05/09): cabe numa linha e responde a pergunta
			    do dia a dia. `descreverCambio` adapta sozinho — cita a validade quando o payload
			    traz `validoAte`, e se cala quando não traz, em vez de prometer prazo que não veio.
			    A janela vem do BE (atualizadoEm/validoAte/congelado, 04/09); o texto antigo dizia
			    "câmbio de hoje", que era falso porque a taxa atravessa a virada do dia. */}
			<span style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
				<span><i className="pi pi-dollar" /> {j.texto} Aplicado aos valores{posReserva ? " das reservas pendentes" : " desta cotação"}.</span>
				<Button text plain style={{ padding: "0", color: "#814DE5", fontSize: "0.78rem", whiteSpace: "nowrap" }}
					icon={detalhe ? "pi pi-chevron-up" : "pi pi-chevron-down"} iconPos="right"
					label={detalhe ? "menos" : "detalhes"} onClick={() => setDetalhe((v) => !v)} />
			</span>
			{detalhe ? (
				<>
					<span style={{ fontSize: "0.8rem", opacity: 0.85 }}>
						<i className="pi pi-info-circle" /> {posReserva
							? "A reserva já está feita no fornecedor; a tarifa não muda — apenas o valor em reais varia com o câmbio, até o pagamento (quando o valor é congelado)."
							: "O câmbio é o da cotação vigente e muda quando a próxima é publicada; até lá os valores em reais não mudam. A tarifa e a disponibilidade dos itens abaixo também podem variar até a reserva ser efetivada."}
					</span>
					{posReserva && temPaga && temPendente ? (
						<span style={{ fontSize: "0.8rem", opacity: 0.85 }}>
							<i className="pi pi-lock" /> Vale só para as reservas <strong>ainda não pagas</strong>. O que já foi pago está com o valor congelado.
						</span>
					) : null}
				</>
			) : null}
		</div>
	);
};

const AvisoAprovacaoVelha = ({ quote }) => {
	if (quote.status !== QUOTE_STATUS_F2.APROVADA || !quote.aprovadaEm) return null;
	const dias = (Date.now() - new Date(quote.aprovadaEm).getTime()) / (24 * 3600 * 1000);
	if (dias < 1) return null;
	return <Message severity="warn" style={{ width: "100%", marginTop: 12 }}
		text={`Esta cotação foi aprovada há ${Math.floor(dias)} dia(s). Preços/câmbio podem ter mudado — re-cotamos ao vivo no passo de reserva.`} />;
};

// Quantas opções da cotação original ficaram de fora da escolha do cliente.
const contarDescartadas = (quote) => {
	const sel = quote?.selecaoCliente || {};
	return agruparTrechos(quote).reduce((n, t) => n + (t.opcoes || []).filter((o) => o.id !== sel[t.id]).length, 0);
};

/**
 * Toggle "ver a cotação original" (owner 2026-08-06). Numa venda ganha/perdida a agência
 * precisa reler o que foi OFERECIDO, não só o que sobrou depois da escolha — para entender
 * a perda e para retomar o cliente. Fica só no admin; a página pública do cliente segue
 * mostrando apenas a seleção dele.
 */
const ToggleOriginal = ({ quote, aberto, onToggle }) => {
	const n = contarDescartadas(quote);
	if (!n) return null;
	return (
		<Button text plain style={{ padding: "2px 0", color: "#814DE5", fontSize: "0.82rem" }}
			icon={aberto ? "pi pi-eye-slash" : "pi pi-eye"}
			label={aberto ? "Ocultar as opções não escolhidas" : `Ver a cotação original (${n} opção(ões) não escolhida(s))`}
			onClick={onToggle} />
	);
};

// Item 12 — seleção aprovada com os dados do hotel e os valores GRAVADOS NA APROVAÇÃO.
//
// O comentário aqui dizia "no fluxo real os valores são re-cotados ao abrir". É FALSO, e era a
// terceira anotação da mesma família a afirmar o contrário do código (as outras duas eram sobre o
// câmbio). Nada re-cota ao abrir fora de rascunho: o painel guarda `podeRecotar = status ===
// RASCUNHO`, e o link público só re-cota em `aguardando_selecao`. Ver valores de hoje é ação
// explícita, pelo PreviewValoresToggle.
const SelecaoAprovada = ({ quote, onPreview }) => {
	const [verOriginal, setVerOriginal] = useState(false);
	// Só ANTES de reservar os valores são re-cotados ao vivo. De `reservada` em diante
	// a cotação é histórico: o que aparece é o snapshot, não uma re-cota.
	const preReserva = quote.status === QUOTE_STATUS_F2.APROVADA;
	const pago = [QUOTE_STATUS_F2.GANHA, QUOTE_STATUS_F2.PARCIALMENTE_GANHA].includes(quote.status);
	// Cotação ainda em `reservada` mas com alguma reserva já paga — os dois regimes convivem.
	const parcialmentePago = (quote.reservas || []).some((r) => r.status === "paga");
	return (
		<div style={{ marginTop: 16 }}>
			<Divider />
			<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
				<strong style={{ color: "#2E0249" }}>Seleção aprovada pelo cliente</strong>
				{preReserva ? (
					<small style={{ color: "#2E9E5B" }}><i className="pi pi-sync" /> disponibilidade, preços e câmbio atualizados agora</small>
				) : pago ? (
					<small style={{ color: "#8A8A8A" }}><i className="pi pi-lock" /> valores congelados no pagamento</small>
				) : parcialmentePago ? (
					// Reservada com parte das reservas já paga: desde 2026-08-07 o BE congela POR
					// RESERVA, então as duas coisas convivem na mesma cotação. Dizer só "vale o
					// cotação vigente" seria falso para o que já foi pago.
					<small style={{ color: "#8A8A8A" }}><i className="pi pi-dollar" /> o que já foi pago está congelado; o restante segue a cotação de câmbio vigente, até o pagamento</small>
				) : (
					// Reservada sem nenhum pagamento: tarifa e disponibilidade travadas no fornecedor,
					// mas o valor em BRL acompanha o câmbio do dia até o pagamento.
					<small style={{ color: "#8A8A8A" }}><i className="pi pi-dollar" /> tarifa garantida — valor em reais pela cotação de câmbio vigente, até o pagamento</small>
				)}
				<ToggleOriginal quote={quote} aberto={verOriginal} onToggle={() => setVerOriginal((v) => !v)} />
			</div>
			<PreviewValoresToggle quote={quote} onPreview={onPreview} />
			<QuoteSelectionView quote={quote} selecao={quote.selecaoCliente} readOnly hideBanner
				mostrarDescartadas={verOriginal} desfechoPorTrecho={desfechoPorTrechoDe(quote)} />
		</div>
	);
};

// Cotação perdida (inclusive derivada de reserva cancelada/vencida): a cotação
// vira histórico. Mostramos o conteúdo em leitura — a seleção do cliente, se
// houve aprovação; senão as opções que estavam na vitrine. SEM re-cota (é morta).
const SelecaoPerdida = ({ quote }) => {
	const [verOriginal, setVerOriginal] = useState(false);
	const temSelecao = quote.selecaoCliente && Object.keys(quote.selecaoCliente).length > 0;
	return (
		<div style={{ marginTop: 16 }}>
			<Divider />
			<strong style={{ color: "#2E0249", display: "block", marginBottom: 4 }}>
				{temSelecao ? "Seleção do cliente nesta cotação (perdida)" : "Opções que estavam nesta cotação (perdida)"}
			</strong>
			{/* Sem seleção a vitrine já mostra tudo — o toggle só existe quando há o que revelar. */}
			{temSelecao ? (
				<div style={{ marginBottom: 6 }}>
					<ToggleOriginal quote={quote} aberto={verOriginal} onToggle={() => setVerOriginal((v) => !v)} />
				</div>
			) : null}
			<QuoteSelectionView quote={quote} historico
				selecao={temSelecao ? quote.selecaoCliente : undefined}
				readOnly={temSelecao} preview={!temSelecao} hideBanner
				mostrarDescartadas={temSelecao && verOriginal} desfechoPorTrecho={desfechoPorTrechoDe(quote)} />
		</div>
	);
};

// Item 9 — reenviar o e-mail podendo manter ou alterar o destinatário.
const ReenviarModal = ({ open, onHide, quote, say, fail, onReenviar }) => {
	const [email, setEmail] = useState("");
	const [busy, setBusy] = useState(false);
	useEffect(() => { setEmail(quote?.contato?.email || ""); }, [open, quote]);
	const valido = /\S+@\S+\.\S+/.test(email);
	return (
		<Dialog header="Reenviar e-mail com o link" visible={open} onHide={onHide} style={{ width: "min(460px, 92vw)" }} blockScroll>
			<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<small style={{ color: "#666" }}>Confirme o destinatário — mantenha o mesmo ou informe outro e-mail.</small>
				<span className="p-float-label"><InputText id="re" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: "100%" }} /><label htmlFor="re">E-mail do cliente *</label></span>
				<Button label="Reenviar e-mail" icon="pi pi-envelope" disabled={!valido || busy} loading={busy} style={roxo}
					onClick={() => { setBusy(true); onReenviar(quote.id, email).then(() => { setBusy(false); onHide(); say(`E-mail reenviado para ${email}`); }).catch((e) => { setBusy(false); fail && fail(e); }); }} />
			</div>
		</Dialog>
	);
};

// Item 4 — histórico de eventos. Vocabulário oficial do BE (14 tipos); cada evento
// vem { tipo, em, meta }. O FE monta o texto (a cópia de UI é nossa).
const EVENTO_META = {
	// §A2 (BE 2026-08-04) — ciclo de vida do rascunho e baixa por reserva.
	criada:             { icon: "pi-plus-circle",   cor: "#814DE5" },
	item_adicionado:    { icon: "pi-plus",          cor: "#814DE5" },
	item_removido:      { icon: "pi-minus",         cor: "#B26A00" },
	renomeada:          { icon: "pi-pencil",        cor: "#814DE5" },
	reserva_paga:       { icon: "pi-dollar",        cor: "#2E9E5B" },
	reserva_cancelada:  { icon: "pi-ban",           cor: "#C0392B" },
	voucher_enviado:    { icon: "pi-envelope",      cor: "#2E9E5B" },
	enviada:            { icon: "pi-send",          cor: "#814DE5" },
	avancada_sem_email: { icon: "pi-forward",       cor: "#814DE5" },
	reenviada:          { icon: "pi-envelope",      cor: "#814DE5" },
	envio_desfeito:     { icon: "pi-undo",          cor: "#B26A00" },
	reaberta_ajuste:    { icon: "pi-pencil",        cor: "#B26A00" },
	perdida:            { icon: "pi-times-circle",  cor: "#C0392B" },
	reaberta:           { icon: "pi-refresh",       cor: "#B26A00" },
	visualizada:        { icon: "pi-eye",           cor: "#2E9E5B" },
	aprovada:           { icon: "pi-check-circle",  cor: "#2E9E5B" },
	selecao_reaberta:   { icon: "pi-pencil",        cor: "#B26A00" },
	reservada:          { icon: "pi-calendar-plus", cor: "#814DE5" },
	ganha:              { icon: "pi-trophy",        cor: "#2E9E5B" },
	parcialmente_ganha: { icon: "pi-star-half",     cor: "#B26A00" },
	perdida_derivada:   { icon: "pi-ban",           cor: "#C0392B" },
};
// Autor do evento (BE: actor = agencia | cliente | sistema).
const ATOR_LABEL = { agencia: "agência", cliente: "cliente", sistema: "sistema" };
// O BE devolve `actor: e.actor || 'sistema'` — ou seja, evento antigo (anterior à coluna)
// chega como "sistema" sem ter sido. Só mostramos autoria quando ela é informativa:
// nome do usuário, ou um ator explícito que não seja o default.
const autorDoEvento = (ev) => ev.usuarioNome || (ev.actor && ev.actor !== "sistema" ? ATOR_LABEL[ev.actor] || ev.actor : "");
// Humaniza um slug desconhecido: "avancada_sem_email" -> "Avancada sem email".
const humanizeTipo = (s) => (s ? String(s).replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase()) : "");
// Monta o texto do evento a partir de tipo + meta (contrato do BE).
const eventoTexto = (ev) => {
	const tipo = ev.tipo || ev.type || "";
	const m = ev.meta || {};
	// Período curto "10/08 → 13/08" a partir do meta dos eventos de item.
	const periodoCurto = (a, b) => {
		const d = (s) => (s && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(5, 10).split("-").reverse().join("/") : "");
		return d(a) && d(b) ? ` (${d(a)} → ${d(b)})` : "";
	};
	switch (tipo) {
		// §A2 — meta: criada {nome}; item_* {itemId,hotelNome,checkIn,checkOut};
		// renomeada {de,para}; reserva_* {bookingId}.
		case "criada": return m.nome ? `Cotação criada — "${m.nome}"` : "Cotação criada";
		case "item_adicionado": return `Opção adicionada${m.hotelNome ? `: ${m.hotelNome}` : ""}${periodoCurto(m.checkIn, m.checkOut)}`;
		case "item_removido": return `Opção removida${m.hotelNome ? `: ${m.hotelNome}` : ""}${periodoCurto(m.checkIn, m.checkOut)}`;
		case "renomeada": return m.de && m.para ? `Renomeada de "${m.de}" para "${m.para}"` : "Cotação renomeada";
		case "reserva_paga": return `Reserva paga${m.bookingId ? ` (#${m.bookingId})` : ""}`;
		case "reserva_cancelada": return `Reserva cancelada${m.bookingId ? ` (#${m.bookingId})` : ""}`;
		case "voucher_enviado": return `Voucher enviado por e-mail${m.bookingId ? ` (reserva #${m.bookingId})` : ""}`;
		case "enviada": return m.email ? `E-mail enviado para ${m.email}` : "Enviada para seleção";
		case "avancada_sem_email": return "Avançada para seleção (sem e-mail)";
		case "reenviada": return m.email ? `Link reenviado para ${m.email}` : "Link reenviado";
		case "envio_desfeito": return "Envio desfeito — voltou para rascunho";
		case "reaberta_ajuste": return `Reaberta para ajuste${m.destino ? ` (→ ${quoteF2StatusLabel(m.destino)})` : ""}`;
		case "perdida": return `Perdida — ${quoteLossMotivoLabel(m.motivo) || m.motivo || "motivo não informado"}`;
		case "reaberta": return "Reaberta — voltou para rascunho";
		case "visualizada": return "Cliente visualizou o link";
		case "aprovada": {
			// BE 2026-08-03/04: meta traz selecao/parcial/totais, sudo e master.
			// sudo=true → não foi o cliente; master distingue QUEM operou:
			// true = master (SMI) operando por uma agência; false = a própria agência.
			const quem = m.sudo
				? (m.master ? "Aprovada pela SMI operando pela agência" : "Aprovada pela agência (escolhendo para o cliente)")
				: "Aprovada pelo cliente";
			const blocos = m.selecao ? Object.keys(m.selecao).length : 0;
			return m.parcial ? `${quem} — aprovação PARCIAL${blocos ? ` (${blocos} bloco(s) selecionado(s))` : ""}` : quem;
		}
		case "selecao_reaberta": return "Cliente refez a seleção";
		case "reservada": return `${Array.isArray(m.reservas) ? m.reservas.length : (m.reservas || 0)} reserva(s) criada(s)`;
		case "ganha": return "Venda ganha — todas as reservas pagas";
		case "parcialmente_ganha": return "Parcialmente ganha — parte paga, parte cancelada";
		case "perdida_derivada": return "Perdida — reservas canceladas/vencidas";
		default: return ev.detalhe || ev.descricao || ev.description || humanizeTipo(tipo) || "Evento";
	}
};
const fmtDataHora = (iso) => {
	if (!iso) return "";
	const d = new Date(iso);
	if (isNaN(d)) return "";
	return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const HistoricoEventos = ({ quote }) => {
	const [aberto, setAberto] = useState(false);
	const eventos = quote?.eventos || [];
	if (!eventos.length) return null;
	return (
		<div style={{ marginTop: 18 }}>
			<Button text plain style={{ padding: "4px 0", color: "#8A8A8A", fontSize: "0.82rem" }}
				icon={aberto ? "pi pi-chevron-down" : "pi pi-chevron-right"}
				label={`Histórico de eventos (${eventos.length})`} onClick={() => setAberto((v) => !v)} />
			{aberto ? (
				<div style={{ borderLeft: "2px solid #EEE", marginLeft: 6, paddingLeft: 12, marginTop: 6 }}>
					{eventos.slice().reverse().map((ev, i) => {
						const tipo = ev.tipo || ev.type || ev.evento || "";
						const meta = EVENTO_META[tipo] || { icon: "pi-circle", cor: "#999" };
						const detalhe = eventoTexto(ev);
						const ts = ev.em || ev.createdAt || ev.data || ev.date || ev.timestamp || ev.at;
						return (
							<div key={i} style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 8 }}>
								<i className={`pi ${meta.icon}`} style={{ color: meta.cor, fontSize: "0.8rem" }} />
								<div style={{ minWidth: 0 }}>
									<div style={{ fontSize: "0.85rem", color: "#333" }}>{detalhe}</div>
									<div style={{ fontSize: "0.72rem", color: "#AAA" }}>
										{fmtDataHora(ts)}
										{/* Autor do evento — o BE grava (UserId/actor); aparece assim que
										    for exposto no retrieve. Inerte enquanto não vier. */}
										{autorDoEvento(ev) ? ` · ${autorDoEvento(ev)}` : ""}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			) : null}
		</div>
	);
};

const FORMA_PAGAMENTO_LABEL = { cartao: "Cartão de crédito", pix: "PIX", outro: "Outra forma" };

const reservaTag = (status) =>
	status === "paga" ? { label: "paga", severity: "success" }
		: status === "cancelada" ? { label: "cancelada", severity: "danger" }
		: { label: "aguardando pagamento", severity: "warning" };

// BUG-004: `prazoPagamento`, `pagoEm` e `canceladaEm` chegam como INSTANTE — fatiar os 10 primeiros
// caracteres mostrava o dia em UTC (um a mais nos horários da noite). `formatDateBR` trata os dois
// formatos; para DATEONLY (check-in/check-out) o resultado é idêntico ao de antes.
const fmtDiaBR = (d) => formatDateBR(d);

/**
 * Bloco de Reservas do painel da agência.
 *
 * Layout tabulado (owner 2026-08-06): uma linha por reserva em grid `1fr auto` — descrição
 * à esquerda, dinheiro/estado à DIREITA e alinhados entre si, para a leitura correr na
 * vertical quando há mais de uma reserva. Antes era um flex com tudo "jogado".
 *
 * PERSISTÊNCIA: hoje estes dados só aparecem logo após criar as reservas, porque vêm da
 * resposta do `proceed-reservation` (fallback `reservasExtra` em Store/Actions/Quote.js).
 * O `QuoteService.retrieve` não devolve `reservas[]`, então ao reabrir a cotação o bloco
 * some — pedido no handoff `frontend-reservas-no-retrieve-agencia-2026-08-05`.
 */
const Reservas = ({ quote, say, allowDevSim, onSimular }) => {
	const reservas = quote.reservas || [];
	// Placar só quando há mais de uma reserva E desfechos diferentes: é o caso em que o
	// status do topo ("Venda parcialmente ganha") não conta a história toda.
	const pagas = reservas.filter((r) => r.status === "paga").length;
	const canceladas = reservas.filter((r) => r.status === "cancelada").length;
	const pendentes = reservas.length - pagas - canceladas;
	const placar = reservas.length > 1
		? [pagas ? `${pagas} paga(s)` : "", pendentes ? `${pendentes} aguardando pagamento` : "", canceladas ? `${canceladas} cancelada(s)` : ""].filter(Boolean).join(" · ")
		: "";
	return (
	<div style={{ marginTop: 16 }}>
		<Divider />
		<div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
			<strong style={{ color: "#2E0249" }}>Reservas{reservas.length > 1 ? ` (${reservas.length})` : ""}</strong>
			{placar ? <small style={{ color: "#6B6B6B" }}>{placar}</small> : null}
		</div>
		{reservas.map((r, i) => {
			const tg = reservaTag(r.status);
			return (
				<div key={r.id} style={reservaLinha}>
					<div style={{ minWidth: 0 }}>
						<div style={{ fontWeight: 600, color: "#2E0249" }}>
							{reservas.length > 1 ? <span style={{ color: "#AAA", fontWeight: 400, marginRight: 6 }}>{i + 1}.</span> : null}
							{r.hotel}{r.quarto ? <span style={{ fontWeight: 400, color: "#6B6B6B" }}> — {r.quarto}</span> : null}
						</div>
						{/* Campos que hoje o BE só devolve no payload PÚBLICO — renderizam
						    sozinhos quando entrarem também no retrieve da agência. */}
						{r.periodo?.checkIn ? (
							<div style={reservaDetalhe}>{fmtDiaBR(r.periodo.checkIn)} → {fmtDiaBR(r.periodo.checkOut)}</div>
						) : null}
						{r.codigoReserva ? (
							<div style={reservaDetalhe}>localizador <strong>{r.codigoReserva}</strong></div>
						) : null}
						{r.status === "paga" && (r.formaPagamento || r.parcelas) ? (
							<div style={{ ...reservaDetalhe, color: "#2E9E5B" }}>
								{FORMA_PAGAMENTO_LABEL[r.formaPagamento] || r.formaPagamento || ""}
								{r.parcelas > 1 ? ` · ${r.parcelas}x` : r.parcelas === 1 ? " · à vista" : ""}
								{(r.valorParcela ?? null) !== null && r.parcelas > 1 ? ` de ${formatNumberCurrency(r.valorParcela, "BRL")}` : ""}
							</div>
						) : null}
						{r.status === "cancelada" && r.motivoCancelamento ? (
							<div style={{ ...reservaDetalhe, color: "#C0392B" }}>
								Motivo: {r.motivoCancelamento}
								{/* Texto livre do operador: só existe no painel autenticado, nunca no público. */}
								{r.motivoCancelamentoDescricao ? ` — ${r.motivoCancelamentoDescricao}` : ""}
							</div>
						) : null}
						{r.hotelViewToken ? (
							<Button label="Ver detalhes do hotel" icon="pi pi-external-link" iconPos="right" link style={{ padding: 0, fontSize: "0.8rem" }}
								onClick={() => window.open(`${publicBaseUrl()}/hotel/${r.hotelViewToken}`, "_blank", "noopener")} />
						) : null}
						{/* O cliente liga pedindo o voucher e a agência resolve na hora, sem mandá-lo
						    ao link. Rota AUTENTICADA por quoteId (não a pública por token): assim a
						    timeline grava `actor: agencia` + o operador, em vez de creditar o envio
						    ao cliente. O PDF continua indo ao e-mail do responsável cadastrado. */}
						{r.voucherDisponivel ? (
							<VoucherButton enviar={() => QuoteActions.enviarVoucherReserva(quote.id, r.bookingId || r.id)}
								label="Enviar voucher ao cliente por e-mail"
								labelFeito="Voucher enviado ao e-mail do cliente." />
						) : null}
					</div>
					{/* Coluna da direita: valor, estado e data — alinhados à direita e entre si. */}
					<div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
						{(r.precoBRL ?? null) !== null ? (
							<div style={{ fontWeight: 700, color: "#814DE5" }}>{formatNumberCurrency(r.precoBRL, "BRL")}</div>
						) : null}
						{/* Valor da reserva × valor cobrado: divergem quando a agência ANTECIPA A
						    COMISSÃO (a comissão sai da cobrança), e o BE marca `comissaoAntecipada`
						    para não termos que adivinhar. Sem esta linha, o operador veria um valor
						    na tela e outro no comprovante do cartão. */}
						{r.comissaoAntecipada && (r.valorCobradoBRL ?? null) !== null ? (
							<div style={reservaDetalhe}>
								cobrado {formatNumberCurrency(r.valorCobradoBRL, "BRL")}
								<div style={{ fontSize: "0.75rem" }}>(comissão antecipada)</div>
							</div>
						) : null}
						<div style={{ marginTop: 4 }}><Tag value={tg.label} severity={tg.severity} /></div>
						{r.status === "pendente" && r.prazoPagamento ? (
							<div style={{ ...reservaDetalhe, color: "#B26A00" }}>pagar até {fmtDiaBR(r.prazoPagamento)}</div>
						) : null}
						{r.status === "paga" && r.pagoEm ? (
							<div style={reservaDetalhe}>pago em {fmtDiaBR(r.pagoEm)}</div>
						) : null}
						{/* A taxa em que este valor travou. É o que responde "por que a outra reserva
						    mudou de preço e esta não?" sem ninguém precisar perguntar. */}
						{r.status === "paga" && r.cambioUsdBrlPago ? (
							<div style={{ ...reservaDetalhe, color: "#2E9E5B" }}>
								<i className="pi pi-lock" style={{ fontSize: "0.7rem", marginRight: 3 }} />
								congelado a R$ {Number(r.cambioUsdBrlPago).toFixed(2)}
							</div>
						) : null}
						{r.status === "cancelada" && r.canceladaEm ? (
							<div style={reservaDetalhe}>cancelada em {fmtDiaBR(r.canceladaEm)}</div>
						) : null}
					</div>
				</div>
			);
		})}
		<small style={{ color: "#999", display: "block", marginTop: 8 }}>
			O pagamento é feito pelo <strong>módulo de reservas</strong> existente (cartão), com baixa <strong>automática</strong>; não há marcação manual aqui.
			O status da cotação deriva das reservas: todas pagas → Ganha · mistura → Parcialmente ganha · todas canceladas → Perdida.
		</small>
		{allowDevSim ? (
			<div style={{ marginTop: 12, padding: 10, border: "1px dashed #CBB8F0", borderRadius: 8, background: "#FAF7FF" }}>
				<div style={{ fontSize: "0.78rem", color: "#7A5FC0", marginBottom: 6 }}>🔧 dev — simular retorno do módulo de reservas (só para validar o funil)</div>
				{quote.reservas.map((r) => (
					<div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
						<small>{r.hotel}</small>
						<span style={{ display: "flex", gap: 4 }}>
							<Button label="pagou" size="small" text onClick={() => onSimular(quote.id, r.id, "paga").then(() => say("(sim) pagamento confirmado"))} />
							<Button label="cancelou" size="small" text severity="danger" onClick={() => onSimular(quote.id, r.id, "cancelada").then(() => say("(sim) reserva cancelada"))} />
						</span>
					</div>
				))}
			</div>
		) : null}
	</div>
	);
};

// Uma reserva por linha: descrição à esquerda, dinheiro/estado à direita (tabulado).
const reservaLinha = { display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "start", padding: "10px 0", borderTop: "1px solid #EEE" };
const reservaDetalhe = { color: "#8A8A8A", fontSize: "0.82rem", marginTop: 2 };

// ---- Modais ---------------------------------------------------------------
const roxo = { background: "#814DE5", border: "none" };
// Confirms um pouco mais estreitos em telas grandes.
const CONFIRM_STYLE = { width: "min(440px, 92vw)" };
const cambioBox = { background: "#EEE6FB", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: "0.88rem", color: "#4A2E7A", display: "flex", flexDirection: "column", gap: 6 };

const SendModal = ({ open, onHide, quote, say, fail, onEnviar }) => {
	const [f, setF] = useState({ nome: "", email: "", documento: "", telefone: "", isForeigner: false });
	const [busy, setBusy] = useState(false);
	const valido = f.nome.trim() && /\S+@\S+\.\S+/.test(f.email);
	return (
		<Dialog header="Enviar cotação para a seleção do cliente" visible={open} onHide={onHide} style={{ width: "min(480px, 92vw)" }} blockScroll>
			<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<span className="p-float-label"><InputText id="n" value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} style={{ width: "100%" }} /><label htmlFor="n">Nome *</label></span>
				<span className="p-float-label"><InputText id="e" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} style={{ width: "100%" }} /><label htmlFor="e">E-mail *</label></span>
				<span className="p-float-label"><InputText id="d" value={f.documento} onChange={(e) => setF({ ...f, documento: e.target.value })} style={{ width: "100%" }} /><label htmlFor="d">CPF / Documento (opcional)</label></span>
				<span className="p-float-label"><InputText id="t" value={f.telefone} onChange={(e) => setF({ ...f, telefone: e.target.value })} style={{ width: "100%" }} /><label htmlFor="t">Telefone (opcional)</label></span>
				<label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: "0.9rem" }}>
					<Checkbox checked={f.isForeigner} onChange={(e) => setF({ ...f, isForeigner: e.checked })} /> Cliente estrangeiro
				</label>
				<small style={{ color: "#999" }}>O CPF do responsável pelo pagamento será coletado depois, no passo de reserva.</small>
				<Button label="Enviar e-mail com o link" icon="pi pi-send" disabled={!valido || busy} loading={busy} style={roxo}
					onClick={() => { setBusy(true); onEnviar(quote.id, f).then(() => { setBusy(false); onHide(); say("Cotação enviada — e-mail disparado ao cliente"); }).catch((e) => { setBusy(false); fail && fail(e); }); }} />
			</div>
		</Dialog>
	);
};

const PerdaModal = ({ open, onHide, quote, say, fail, onPerder }) => {
	const opcoes = QUOTE_LOSS_MOTIVO_OPTIONS[quote.status] || [];
	const [motivo, setMotivo] = useState(null);
	useEffect(() => { setMotivo(null); }, [open]);
	return (
		<Dialog header="Marcar cotação como perdida" visible={open} onHide={onHide} style={{ width: "min(440px, 92vw)" }} blockScroll>
			<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
				<Dropdown value={motivo} options={opcoes} onChange={(e) => setMotivo(e.value)} placeholder="Motivo da perda" style={{ width: "100%" }} />
				<Button label="Confirmar perda" icon="pi pi-times" severity="danger" disabled={!motivo}
					onClick={() => onPerder(quote.id, motivo).then(() => { onHide(); say("Cotação marcada como perdida"); }).catch(fail)} />
			</div>
		</Dialog>
	);
};

const ClienteModal = ({ open, onHide, quote, say, fail, onAprovar }) => {
	const [selecao, setSelecao] = useState(quote.selecaoCliente || {});
	const [busy, setBusy] = useState(false);
	useEffect(() => { setSelecao(quote.selecaoCliente || {}); }, [open, quote]);
	const aprovar = (sel) => {
		setBusy(true);
		onAprovar(quote.id, quote.token, sel)
			.then(() => { setBusy(false); onHide(); say("Aprovada pela agência (para o cliente)"); })
			.catch((e) => { setBusy(false); fail && fail(e); });
	};
	return (
		<Dialog header="Escolher para o cliente" visible={open} onHide={onHide} style={{ width: "min(640px, 94vw)" }} blockScroll>
			<QuoteSelectionView quote={quote} selecao={selecao} onSelecao={setSelecao} aprovando={busy} actorLabel="agência"
				onAprovar={(sel, info) => {
					// Seleção parcial: confirma o descarte dos blocos que ficaram de fora.
					if (info?.parcial) confirmDialog({ ...confirmParcial(info.naoSelecionados), accept: () => aprovar(sel) });
					else aprovar(sel);
				}} />
		</Dialog>
	);
};

// Confirmação de APROVAÇÃO PARCIAL — compartilhada pela agência (SUDO) e espelhada
// na página pública do cliente. O que não foi selecionado não vai para reserva.
export const confirmParcial = (naoSelecionados = []) => ({
	header: "Aprovar apenas parte da cotação",
	icon: "pi pi-exclamation-triangle",
	style: { width: "min(480px, 92vw)" },
	acceptLabel: "Sim, aprovar assim",
	rejectLabel: "Voltar e revisar",
	message: `${naoSelecionados.length} bloco(s) ficaram sem seleção${naoSelecionados.length ? ` (${naoSelecionados.join(", ")})` : ""}. `
		+ "O que não foi selecionado será DESCARTADO e não entrará na reserva. "
		+ "Se ainda quiser esses trechos, será necessário fazer uma nova cotação. Deseja aprovar mesmo assim?",
});

const ReservaModal = ({ open, onHide, quote, say, fail, onProsseguir }) => {
	const [busy, setBusy] = useState(false);
	const [feito, setFeito] = useState(false);
	useEffect(() => { if (open) setFeito(false); }, [open]);
	return (
		<Dialog header={feito ? "Reserva confirmada" : "Prosseguir com reserva"} visible={open} onHide={onHide} style={{ width: "min(760px, 96vw)" }} blockScroll>
			{feito ? (
				<ReservaConfirmada quote={quote} onClose={onHide} />
			) : (
				<QuoteReserveWizard quote={quote} finalizando={busy}
					onFinalizar={(dados) => {
						setBusy(true);
						onProsseguir(quote.id, dados)
							.then(() => { setBusy(false); setFeito(true); say("Reservas criadas — aguardando pagamento"); })
							.catch((e) => { setBusy(false); fail && fail(e); });
					}} />
			)}
		</Dialog>
	);
};

// Item 3 — dados de confirmação da reserva nos moldes do fluxo atual
// (hotelBookingConfirm.js): reserva criada, código, voucher "Aguardando pagamento",
// prazo. O pagamento é etapa separada, feita depois pelo módulo de reservas.
const ReservaConfirmada = ({ quote, onClose }) => {
	const reservas = quote.reservas || [];
	// Idem fmtDiaBR: sem isto, `prazoPagamento` como instante virava "17T03:00:00.000Z/09/2026".
	const fmtData = (d) => formatDateBR(d);
	return (
		<div>
			<Message severity="success" style={{ width: "100%", marginBottom: 12 }}
				text="Reserva(s) criada(s) e enviada(s) à propriedade. O pagamento está pendente e é uma etapa separada — pode ser concluído depois, pelo módulo de reservas, dentro do prazo." />
			{/* A reserva garante tarifa e disponibilidade, não o valor em reais: o câmbio
			    continua correndo até o pagamento (owner 2026-08-04). */}
			<Message severity="info" style={{ width: "100%", marginBottom: 12 }}
				text="A partir de agora a tarifa e a disponibilidade estão garantidas e não mudam mais. O valor em reais, porém, ainda depende do câmbio: ele é recalculado pela cotação de câmbio vigente até o pagamento ser concluído — e só então fica congelado." />
			{reservas.map((r) => (
				<div key={r.id} style={{ display: "flex", gap: 12, border: "1px solid #EEE", borderRadius: 10, padding: 12, marginBottom: 10 }}>
					<img src={r.foto || imagePlaceHolder} alt="" style={{ width: 96, height: 72, objectFit: "cover", borderRadius: 8, flexShrink: 0 }}
						onError={(e) => { e.target.src = imagePlaceHolder; }} />
					<div style={{ flex: 1, minWidth: 0 }}>
						<div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
							<strong style={{ color: "#2E0249" }}>{r.hotel}</strong>
							<Tag value="Aguardando pagamento" severity="warning" />
						</div>
						<div style={{ color: "#6B6B6B", fontSize: "0.85rem", marginTop: 2 }}>
							{r.quarto}{r.regime ? ` · ${r.regime}` : ""}
						</div>
						<div style={{ color: "#8A8A8A", fontSize: "0.82rem" }}>{fmtData(r.periodo?.checkIn)} → {fmtData(r.periodo?.checkOut)}</div>
						{r.temCafe ? <div style={{ fontSize: "0.78rem", marginTop: 2, color: "#1e6b3a" }}><i className="pi pi-check" style={{ fontSize: "0.7rem", marginRight: 4 }} />Café da manhã incluído</div> : null}
						<div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6, fontSize: "0.85rem", color: "#444", alignItems: "center" }}>
							<span>Código: <strong>{r.codigoReserva || "—"}</strong></span>
							<span style={{ fontWeight: 700, color: "#814DE5" }}>{formatNumberCurrency(r.precoBRL, "BRL")}</span>
							{r.prazoPagamento ? <span>Pagar até <strong>{fmtData(r.prazoPagamento)}</strong></span> : null}
						</div>
						{r.hotelViewToken ? (
							<Button label="Ver detalhes do hotel" icon="pi pi-external-link" iconPos="right" link style={{ padding: 0, fontSize: "0.8rem", marginTop: 4 }}
								onClick={() => window.open(`${publicBaseUrl()}/hotel/${r.hotelViewToken}`, "_blank", "noopener")} />
						) : null}
					</div>
				</div>
			))}
			<Button label="Fechar" icon="pi pi-check" onClick={onClose} style={{ width: "100%", marginTop: 4, background: "#814DE5", border: "none" }} />
		</div>
	);
};

const mapState = (s) => ({ selectedQuote: s.quoteReducer?.selectedQuote, masterUser: s.appReducer?.masterUser });
const mapDispatch = (d) => bindActionCreators({
	enviarCotacaoParaSelecao: QuoteActions.enviarCotacaoParaSelecao,
	avancarCotacaoSemEnviar: QuoteActions.avancarCotacaoSemEnviar,
	reenviarEmailCotacao: QuoteActions.reenviarEmailCotacao,
	desfazerEnvioCotacao: QuoteActions.desfazerEnvioCotacao,
	reabrirCotacaoParaAjuste: QuoteActions.reabrirCotacaoParaAjuste,
	marcarCotacaoPerdida: QuoteActions.marcarCotacaoPerdida,
	reabrirCotacaoPerdida: QuoteActions.reabrirCotacaoPerdida,
	aprovarCotacaoComoAgencia: QuoteActions.aprovarCotacaoComoAgencia,
	prosseguirComReserva: QuoteActions.prosseguirComReserva,
	simularStatusReservaCotacao: QuoteActions.simularStatusReservaCotacao,
	removerItemCotacao: QuoteActions.removerItemCotacao,
	recotarCotacaoF2: QuoteActions.recotarCotacaoF2,
	previewValoresCotacao: QuoteActions.previewValoresCotacao,
	deleteQuote: QuoteActions.deleteQuote,
	renameQuote: QuoteActions.renameQuote,
}, d);

export default connect(mapState, mapDispatch)(QuoteFase2Panel);
